import { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  ShoppingCart, 
  Package,
  Calendar,
  Filter
} from 'lucide-react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { formatCurrency } from '@/lib/utils';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';

export function BI() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    carregarBI();
  }, []);

  const carregarBI = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/bi/dashboard');
      setData(data);
    } catch (error: any) {
      console.error('Erro ao carregar BI:', error);
      // Mostrar dados zerados em caso de erro (404 = backend não deployado)
      setData({
        metrics: {
          totalFaturamento: 0,
          totalCusto: 0,
          totalLucro: 0,
          ticketMedio: 0,
          totalPedidos: 0,
          crescimentoPercentual: 0,
          margemMedia: 0
        },
        topProdutos: [],
        vendasPorDia: []
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const { metrics, topProdutos, vendasPorDia } = data;

  return (
    <div className="space-y-8 w-full px-6 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Business Intelligence</h1>
          <p className="text-gray-500">Analise o desempenho da sua indústria 3D</p>
        </div>
        <div className="flex gap-2">
          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50">
            <Calendar className="w-4 h-4" />
            Últimos 30 Dias
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90">
            <Filter className="w-4 h-4" />
            Filtros
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPIItem 
          title="Faturamento Bruto" 
          value={formatCurrency(metrics.totalFaturamento)} 
          change={metrics.crescimentoPercentual}
          icon={<DollarSign className="w-5 h-5 text-green-600" />}
          color="green"
        />
        <KPIItem 
          title="Lucro Líquido" 
          value={formatCurrency(metrics.totalLucro)} 
          change={metrics.margemMedia}
          changeLabel="Margem"
          icon={<TrendingUp className="w-5 h-5 text-blue-600" />}
          color="blue"
        />
        <KPIItem 
          title="Ticket Médio" 
          value={formatCurrency(metrics.ticketMedio)} 
          icon={<ShoppingCart className="w-5 h-5 text-purple-600" />}
          color="purple"
        />
        <KPIItem 
          title="Total Pedidos" 
          value={metrics.totalPedidos} 
          icon={<Package className="w-5 h-5 text-orange-600" />}
          color="orange"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Gráfico de Vendas */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Evolução de Vendas
            </CardTitle>
          </CardHeader>
          <CardContent className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={vendasPorDia}>
                <defs>
                  <linearGradient id="colorValor" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FF6B35" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#FF6B35" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis 
                  dataKey="data" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 12, fill: '#9CA3AF' }}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 12, fill: '#9CA3AF' }}
                  tickFormatter={(val) => `R$${val}`}
                />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  formatter={(val: number) => [formatCurrency(val), 'Vendas']}
                />
                <Area 
                  type="monotone" 
                  dataKey="valor" 
                  stroke="#FF6B35" 
                  strokeWidth={3}
                  fillOpacity={1} 
                  fill="url(#colorValor)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Produtos */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Top 5 Produtos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {topProdutos.map((produto: any, idx: number) => (
                <div key={idx} className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{produto.nome}</p>
                    <p className="text-xs text-gray-500">{produto.quantidadeVendida} unidades vendidas</p>
                  </div>
                  <div className="text-sm font-semibold text-gray-900">
                    {formatCurrency(Number(produto.faturamentoTotal || 0))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KPIItem({ title, value, change, changeLabel = 'Crescimento', icon, color }: any) {
  const colors: any = {
    green: 'bg-green-50',
    blue: 'bg-blue-50',
    purple: 'bg-purple-50',
    orange: 'bg-orange-50'
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-2 rounded-lg ${colors[color]}`}>{icon}</div>
          {change !== undefined && (
            <div className={`flex items-center gap-1 text-xs font-medium ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {Math.abs(change).toFixed(1)}%
              <span className="text-gray-400 ml-1 ml-1">{changeLabel}</span>
            </div>
          )}
        </div>
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <h3 className="text-2xl font-bold text-gray-900 mt-1">{value}</h3>
        </div>
      </CardContent>
    </Card>
  );
}
